import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Globe, 
  Search, 
  Edit3, 
  Save, 
  X, 
  Plus, 
  Trash2, 
  Download, 
  Upload, 
  RefreshCw,
  Languages,
  Eye,
  EyeOff,
  Copy,
  Check
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { useTheme } from '../../../contexts/ThemeContext';
import { api } from '../../../utils/api';

const LabelManager = () => {
  const { user, hasRole } = useAuth();
  const { getProtectedTenantInfo } = useTenantProtection();
  const { isDark } = useTheme();
  
  // Check if user has access (Admin only)
  const hasAccess = hasRole(['Admin']);
  
  // Get current tenant info safely
  const getCurrentTenant = () => {
    try {
      return getProtectedTenantInfo();
    } catch (error) {
      console.error('❌ Failed to get tenant info:', error);
      throw new Error('Invalid tenant context detected');
    }
  };

  const [labels, setLabels] = useState({});
  const [availableLanguages, setAvailableLanguages] = useState(['en']);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLabelKey, setNewLabelKey] = useState('');
  const [newLabelValue, setNewLabelValue] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    if (hasAccess) {
      loadLanguages();
    }
  }, [hasAccess]);

  useEffect(() => {
    if (hasAccess && selectedLanguage) {
      loadLabels();
    }
  }, [hasAccess, selectedLanguage]);

  const loadLanguages = async () => {
    try {
      const response = await api.get('/labels/languages');
      if (response.success) {
        setAvailableLanguages(response.data);
        if (response.data.length > 0 && !response.data.includes(selectedLanguage)) {
          setSelectedLanguage(response.data[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load languages:', error);
      toast.error('Failed to load available languages');
    }
  };

  const loadLabels = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/labels/${selectedLanguage}`);
      if (response.success) {
        setLabels(response.data);
      } else {
        toast.error('Failed to load labels');
      }
    } catch (error) {
      console.error('Failed to load labels:', error);
      toast.error('Failed to load labels');
    } finally {
      setLoading(false);
    }
  };

  const saveLabels = async () => {
    try {
      setSaving(true);
      const response = await api.put(`/labels/${selectedLanguage}`, { labels });
      if (response.success) {
        toast.success('Labels saved successfully');
      } else {
        toast.error('Failed to save labels');
      }
    } catch (error) {
      console.error('Failed to save labels:', error);
      toast.error('Failed to save labels');
    } finally {
      setSaving(false);
    }
  };

  const updateLabel = async (key, value) => {
    try {
      const response = await api.put(`/labels/${selectedLanguage}/${key}`, { value });
      if (response.success) {
        toast.success('Label updated successfully');
        loadLabels(); // Reload to get updated data
      } else {
        toast.error('Failed to update label');
      }
    } catch (error) {
      console.error('Failed to update label:', error);
      toast.error('Failed to update label');
    }
  };

  const deleteLabel = async (key) => {
    if (!window.confirm(`Are you sure you want to delete the label "${key}"?`)) {
      return;
    }

    try {
      const response = await api.delete(`/labels/${selectedLanguage}/${key}`);
      if (response.success) {
        toast.success('Label deleted successfully');
        loadLabels(); // Reload to get updated data
      } else {
        toast.error('Failed to delete label');
      }
    } catch (error) {
      console.error('Failed to delete label:', error);
      toast.error('Failed to delete label');
    }
  };

  const addLabel = async () => {
    if (!newLabelKey || !newLabelValue) {
      toast.error('Please fill in both key and value');
      return;
    }

    try {
      const response = await api.put(`/labels/${selectedLanguage}/${newLabelKey}`, { value: newLabelValue });
      if (response.success) {
        toast.success('Label added successfully');
        setNewLabelKey('');
        setNewLabelValue('');
        setShowAddForm(false);
        loadLabels(); // Reload to get updated data
      } else {
        toast.error('Failed to add label');
      }
    } catch (error) {
      console.error('Failed to add label:', error);
      toast.error('Failed to add label');
    }
  };

  const exportLabels = async () => {
    try {
      const response = await api.post(`/labels/${selectedLanguage}/export`, { format: 'json' });
      if (response) {
        // Create and download file
        const blob = new Blob([response], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `labels-${selectedLanguage}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Labels exported successfully');
      }
    } catch (error) {
      console.error('Failed to export labels:', error);
      toast.error('Failed to export labels');
    }
  };

  const copyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
      toast.success('Key copied to clipboard');
    } catch (error) {
      console.error('Failed to copy key:', error);
      toast.error('Failed to copy key');
    }
  };

  const startEdit = (key, value) => {
    setEditingKey(key);
    setEditingValue(value);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditingValue('');
  };

  const saveEdit = async () => {
    if (editingKey && editingValue !== '') {
      await updateLabel(editingKey, editingValue);
    }
    cancelEdit();
  };

  // Flatten labels for display
  const flattenLabels = (obj, prefix = '') => {
    const result = [];
    for (const key in obj) {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        result.push(...flattenLabels(obj[key], prefix ? `${prefix}.${key}` : key));
      } else {
        result.push({
          key: prefix ? `${prefix}.${key}` : key,
          value: obj[key]
        });
      }
    }
    return result;
  };

  const filteredLabels = flattenLabels(labels).filter(label =>
    label.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    label.value.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="mx-auto h-8 w-8 animate-spin text-blue-600" />
          <p className="mt-2 text-sm text-gray-600">Loading labels...</p>
        </div>
      </div>
    );
  }

  // Show access denied if user doesn't have permission
  if (!hasAccess) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <Globe className="mx-auto h-12 w-12 text-yellow-400" />
          <h3 className={`mt-2 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Access Denied</h3>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
            You need Admin permissions to access Label Manager.
          </p>
        </div>
      </div>
    );
  }

  const currentTenant = getCurrentTenant();

  return (
    <div className={`space-y-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold flex items-center space-x-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Languages className="h-7 w-7 text-blue-600" />
            <span>Label Manager</span>
          </h1>
          <p className={`mt-1 flex items-center space-x-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            <Globe className="h-4 w-4" />
            <span>Manage application labels and translations</span>
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadLabels}
            disabled={loading}
            className={`flex items-center space-x-2 px-4 py-2 border rounded-md disabled:opacity-50 ${
              isDark 
                ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={exportLabels}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button
            onClick={saveLabels}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{saving ? 'Saving...' : 'Save All'}</span>
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Language Selector */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Language
              </label>
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isDark 
                    ? 'border-gray-600 bg-gray-700 text-white' 
                    : 'border-gray-300 bg-white text-gray-900'
                }`}
              >
                {availableLanguages.map(lang => (
                  <option key={lang} value={lang}>
                    {lang === 'en' ? 'English' : lang === 'th' ? 'ไทย' : lang.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Search Labels
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    isDark 
                      ? 'border-gray-600 bg-gray-700 text-white' 
                      : 'border-gray-300 bg-white text-gray-900'
                  }`}
                  placeholder="Search by key or value..."
                />
              </div>
            </div>

            {/* Add New Label */}
            <div className="flex items-end">
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                <Plus className="h-4 w-4" />
                <span>Add Label</span>
              </button>
            </div>
          </div>

          {/* Add New Label Form */}
          {showAddForm && (
            <div className="border-t pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Label Key (e.g., nav.dashboard)
                  </label>
                  <input
                    type="text"
                    value={newLabelKey}
                    onChange={(e) => setNewLabelKey(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white' 
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    placeholder="nav.newFeature"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Label Value
                  </label>
                  <input
                    type="text"
                    value={newLabelValue}
                    onChange={(e) => setNewLabelValue(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white' 
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    placeholder="New Feature"
                  />
                </div>
                <div className="flex items-end space-x-2">
                  <button
                    onClick={addLabel}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <Save className="h-4 w-4" />
                    <span>Add</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowAddForm(false);
                      setNewLabelKey('');
                      setNewLabelValue('');
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    <X className="h-4 w-4" />
                    <span>Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Labels List */}
      <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <h2 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Labels ({filteredLabels.length} found)
          </h2>
          
          {filteredLabels.length === 0 ? (
            <div className="text-center py-8">
              <Globe className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className={`mt-2 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                No labels found
              </h3>
              <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {searchQuery ? 'Try adjusting your search query' : 'No labels available for this language'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredLabels.map((label) => (
                <div
                  key={label.key}
                  className={`flex items-center space-x-4 p-3 border rounded-md ${
                    isDark 
                      ? 'border-gray-600 bg-gray-700' 
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    {editingKey === label.key ? (
                      <input
                        type="text"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        className={`w-full px-2 py-1 border rounded text-sm ${
                          isDark 
                            ? 'border-gray-500 bg-gray-600 text-white' 
                            : 'border-gray-300 bg-white text-gray-900'
                        }`}
                        autoFocus
                      />
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {label.key}:
                        </span>
                        <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                          {label.value}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {editingKey === label.key ? (
                      <>
                        <button
                          onClick={saveEdit}
                          className="p-1 text-green-600 hover:text-green-700"
                          title="Save"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-1 text-gray-600 hover:text-gray-700"
                          title="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => copyKey(label.key)}
                          className="p-1 text-blue-600 hover:text-blue-700"
                          title="Copy key"
                        >
                          {copiedKey === label.key ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => startEdit(label.key, label.value)}
                          className="p-1 text-blue-600 hover:text-blue-700"
                          title="Edit"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteLabel(label.key)}
                          className="p-1 text-red-600 hover:text-red-700"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LabelManager;
